import React, { useCallback, useEffect, useState } from 'react';
import { equipmentService } from '../services/dataService';
import { formatDateTime, StatusPill } from './DetailDashboard';
import Pagination from './Pagination';
import styles from '../pages/assets/css/DetailDashboard.module.css';

const PAGE_SIZE = 10;
const SPS_KEYS = ['ScheduledProcedureStepSequence', '0040,0100'];
const RESULT_FILTERS = [
  { value: '', label: 'All queries' },
  { value: 'true', label: 'Returned worklists' },
  { value: 'false', label: 'Returned nothing' },
];

const firstValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return firstValue(value.Value);
  if (Array.isArray(value)) return firstValue(value[0]);
  return value == null ? '' : String(value);
};

/** Short description of the filters a machine used in its worklist query. */
const describeQuery = (query) => {
  const step = SPS_KEYS.map((key) => query?.[key]).find(Array.isArray)?.[0] || {};
  const pick = (source, ...keys) => keys.map((key) => firstValue(source?.[key])).find(Boolean) || '';
  const parts = [
    ['Modality', pick(step, 'Modality', '0008,0060') || pick(query, 'Modality', '0008,0060')],
    ['Date', pick(step, 'ScheduledProcedureStepStartDate', '0040,0002')],
    ['Station AE', pick(step, 'ScheduledStationAETitle', '0040,0001')],
    ['Accession', pick(query, 'AccessionNumber', '0008,0050')],
    ['Patient', pick(query, 'PatientID', '0010,0020')],
  ].filter(([, value]) => value && value !== '*');
  return parts.length ? parts.map(([label, value]) => `${label} ${value}`).join(' · ') : 'No filters (all worklists)';
};

/**
 * Paginated table of a machine's MWL C-FIND queries and what each returned.
 * `refreshKey` changes when the parent wants the current page reloaded.
 */
const WorklistQueriesTable = ({ equipmentId, refreshKey = 0 }) => {
  const [page, setPage] = useState(1);
  const [returned, setReturned] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await equipmentService.getWorklistQueries(equipmentId, {
        page,
        page_size: PAGE_SIZE,
        returned: returned || undefined,
      }));
    } catch (err) {
      setError(err?.detail || 'Failed to load worklist queries');
    } finally {
      setLoading(false);
    }
  }, [equipmentId, page, returned]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>Worklist Queries</h2>
        <select
          className={styles.filterInput}
          value={returned}
          onChange={(e) => { setReturned(e.target.value); setPage(1); }}
          aria-label="Filter worklist queries by result"
        >
          {RESULT_FILTERS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Machine</th>
              <th>Result</th>
              <th>Worklists returned</th>
              <th>Query filters</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((event) => (
              <tr key={event.id}>
                <td className={styles.nowrap}>{formatDateTime(event.captured_at)}</td>
                <td>
                  <code>{event.ae_title}</code>
                  <span className={styles.subText}>{event.ip_addr || '—'}</span>
                </td>
                <td>
                  <StatusPill
                    value={event.matched_count ? 'active' : 'unknown'}
                    label={event.matched_count ? `Pulled ${event.matched_count}` : 'Nothing returned'}
                  />
                </td>
                <td>{event.matched_accessions?.length ? event.matched_accessions.join(', ') : '—'}</td>
                <td className={styles.muted}>{describeQuery(event.query)}</td>
              </tr>
            ))}
            {!loading && data.items.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  {returned
                    ? 'No worklist queries match this filter.'
                    : 'No worklist queries received from this machine yet. Machines normally query every few minutes while powered on.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} loading={loading} onPageChange={setPage} />
    </section>
  );
};

export default WorklistQueriesTable;
